export type EventTypes = {
  // Core events
  'core-join-meeting': {};

  // Breakouts events
  'br-update-ask-for-help': { needHelp?: boolean };

  // Hand raising events
  'hr-force-lower-hand': {
    isLoweredAutomatically?: boolean;
    sessionId: string;
  };
  'hr-lower-hand': {};
  'hr-raise-hand': {};
};

export type EventCallback<T extends keyof EventTypes> = (
  data: EventTypes[T]
) => void;

interface QueuedEventData<T extends keyof EventTypes> {
  eventType: T;
  data: EventTypes[T];
}

const noop = () => {};

/**
 * EventBridge enables dynamic event subscription and decouples features like breakouts and hand raising.
 * For example, the breakout feature can emit events to automatically lower raised hands without importing hand raising logic.
 * This promotes modularity and loose coupling.
 */
class EventBridge {
  private eventSubscribers: { [eventType: string]: Array<EventCallback<any>> } =
    {};
  private queuedEvents: { [eventType: string]: Array<QueuedEventData<any>> } =
    {};

  public subscribe<T extends keyof EventTypes>(
    eventType: T,
    callback: EventCallback<T>
  ): void {
    if (!this.eventSubscribers[eventType]) {
      this.eventSubscribers[eventType] = [];
    }
    this.eventSubscribers[eventType].push(callback ?? noop);
    this.flushQueue(eventType); // Check if there are any queued events for this event type
  }

  public unsubscribe<T extends keyof EventTypes>(
    eventType: T,
    callback: EventCallback<T>
  ): void {
    const subscribers = this.eventSubscribers[eventType];
    if (subscribers) {
      const index = subscribers.indexOf(callback);
      if (index !== -1) {
        subscribers.splice(index, 1);
      }
    }
  }

  public emit<T extends keyof EventTypes>(
    eventType: T,
    data: EventTypes[T]
  ): void {
    const subscribers = this.eventSubscribers[eventType];
    if (subscribers && subscribers.length > 0) {
      subscribers.forEach((callback) => {
        callback(data);
      });
    } else {
      if (!this.queuedEvents[eventType]) {
        this.queuedEvents[eventType] = [];
      }
      this.queuedEvents[eventType].push({ eventType, data });
      if (this.queuedEvents[eventType].length > 10) {
        // Limit the number of unemitted event data objects to avoid infinite accumulation
        this.queuedEvents[eventType].shift();
      }
    }
  }

  public flushQueue<T extends keyof EventTypes>(eventType: T): void {
    const subscribers = this.eventSubscribers[eventType];
    const queuedEvents = this.queuedEvents[eventType];
    if (
      subscribers &&
      subscribers.length > 0 &&
      queuedEvents &&
      queuedEvents.length > 0
    ) {
      queuedEvents.forEach((queuedEvent) => {
        subscribers.forEach((callback) => {
          callback(queuedEvent.data);
        });
      });
      delete this.queuedEvents[eventType]; // Remove the queued event data for this event type
    }
  }
}

export default new EventBridge();
